import { useState, useMemo } from "react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay, addDays, subDays, startOfWeek, endOfWeek, addMonths, subMonths, getDay } from "date-fns";
import { de } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import { Lead, LeadStatus } from "@/components/lead-card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface LeadCalendarProps {
  leads: Lead[];
  viewMode: "month" | "week" | "day";
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  onLeadClick?: (lead: Lead) => void;
}

const STATUS_LABELS: Record<LeadStatus, string> = {
  "new": "Neu",
  "in-progress": "In Bearbeitung",
  "callback": "Rückruf geplant",
  "offer": "Angebot erstellt",
  "negotiation": "Verhandlung",
  "won": "Gewonnen",
  "lost": "Verloren",
  "paused": "Pausiert",
  "wrong-contact": "Falsche Kontaktdaten",
  "delete-contact": "Kontakt Löschen"
};

const STATUS_CLASSES: Record<LeadStatus, string> = {
  "new": "bg-blue-100 text-blue-800 border-blue-300",
  "in-progress": "bg-yellow-100 text-yellow-800 border-yellow-300",
  "callback": "bg-purple-100 text-purple-800 border-purple-300",
  "offer": "bg-teal-100 text-teal-800 border-teal-300",
  "negotiation": "bg-amber-100 text-amber-800 border-amber-300",
  "won": "bg-green-100 text-green-800 border-green-300",
  "lost": "bg-red-100 text-red-800 border-red-300",
  "paused": "bg-gray-100 text-gray-800 border-gray-300",
  "wrong-contact": "bg-orange-100 text-orange-800 border-orange-300",
  "delete-contact": "bg-red-200 text-red-900 border-red-400"
};

const getLeadsForDate = (leads: Lead[], date: Date) => {
  return leads.filter(lead => {
    if (!lead.nextContactDate) return false;
    const nextContactDate = new Date(lead.nextContactDate);
    return isSameDay(nextContactDate, date);
  }).sort((a, b) => {
    // Sort by time if available
    const timeA = a.nextContactDate ? new Date(a.nextContactDate).getTime() : 0;
    const timeB = b.nextContactDate ? new Date(b.nextContactDate).getTime() : 0;
    return timeA - timeB;
  });
};

export function LeadCalendar({ leads, viewMode, selectedDate, onDateSelect, onLeadClick }: LeadCalendarProps) {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());

  // Navigate to the next or previous period based on view mode
  const navigatePeriod = (direction: "prev" | "next") => {
    if (viewMode === "month") {
      setCurrentDate(direction === "next" ? addMonths(currentDate, 1) : subMonths(currentDate, 1));
    } else if (viewMode === "week") {
      setCurrentDate(direction === "next" ? addDays(currentDate, 7) : subDays(currentDate, 7));
    } else {
      setCurrentDate(direction === "next" ? addDays(currentDate, 1) : subDays(currentDate, 1));
    }
  };

  // Handle lead click - use prop function if provided
  const handleLeadClick = (lead: Lead) => {
    if (onLeadClick) {
      onLeadClick(lead);
    } else {
      console.log("Navigate to lead:", lead);
    }
  };

  // Get days for the current month view
  const monthDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart, { locale: de });
    const endDate = endOfWeek(monthEnd, { locale: de });
    
    return eachDayOfInterval({ start: startDate, end: endDate });
  }, [currentDate, viewMode]);

  // Get days for the current week view
  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(currentDate, { locale: de });
    const weekEnd = endOfWeek(currentDate, { locale: de });
    
    return eachDayOfInterval({ start: weekStart, end: weekEnd });
  }, [currentDate, viewMode]);

  // Generate month view calendar
  const renderMonthView = () => {
    const dayNames = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
    
    return (
      <div className="bg-white rounded-lg shadow">
        {/* Calendar header */}
        <div className="grid grid-cols-7 gap-px border-b text-center">
          {dayNames.map((day) => (
            <div key={day} className="py-2 font-semibold text-sm">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-px">
          {monthDays.map((day, i) => {
            const dayLeads = getLeadsForDate(leads, day);
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isSelected = isSameDay(day, selectedDate);
            
            return (
              <div
                key={i}
                className={cn(
                  "min-h-[100px] p-2 border",
                  !isCurrentMonth && "bg-gray-50 text-gray-400",
                  isToday(day) && "bg-blue-50",
                  isSelected && "ring-2 ring-primary ring-inset"
                )}
                onClick={() => onDateSelect(day)}
              >
                <div className="flex justify-between items-center mb-1">
                  <span className={cn("text-sm font-medium", isToday(day) && "bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center")}>
                    {format(day, "d")}
                  </span>
                  {dayLeads.length > 0 && (
                    <Badge variant="outline" className="bg-primary-foreground">
                      {dayLeads.length}
                    </Badge>
                  )}
                </div>
                
                <div className="space-y-1 max-h-[80px] overflow-y-auto">
                  {dayLeads.slice(0, 3).map((lead) => {
                    const contactTime = lead.nextContactDate 
                      ? format(new Date(lead.nextContactDate), "HH:mm")
                      : "";
                    
                    return (
                      <TooltipProvider key={lead.id}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div 
                              className={cn(
                                "text-xs p-1 truncate rounded border cursor-pointer hover:bg-gray-100",
                                STATUS_CLASSES[lead.status]
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleLeadClick(lead);
                              }}
                            >
                              {contactTime && <span className="font-medium mr-1">{contactTime}</span>}
                              {`${lead.firstName} ${lead.lastName}`}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="text-xs">
                              <p className="font-bold">{`${lead.firstName} ${lead.lastName}`}</p>
                              <p>{lead.company}</p>
                              {contactTime && <p className="mt-1">Kontakt: {contactTime} Uhr</p>}
                              {lead.attachment && <p>Anhänger: {lead.attachment}</p>}
                              <Badge className="mt-1" variant="secondary">{STATUS_LABELS[lead.status]}</Badge>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })}
                  {dayLeads.length > 3 && (
                    <div className="text-xs text-gray-500 italic">
                      +{dayLeads.length - 3} weitere
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Generate week view calendar
  const renderWeekView = () => {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="grid grid-cols-1 divide-y">
          {weekDays.map((day, i) => {
            const dayLeads = getLeadsForDate(leads, day);
            const isSelected = isSameDay(day, selectedDate);
            
            return (
              <div 
                key={i} 
                className={cn(
                  "p-4",
                  isToday(day) && "bg-blue-50",
                  isSelected && "ring-2 ring-primary ring-inset"
                )}
                onClick={() => onDateSelect(day)}
              >
                <div className="flex items-center mb-3">
                  <div className={cn(
                    "w-12 h-12 rounded-full flex items-center justify-center mr-4",
                    isToday(day) ? "bg-primary text-white" : "bg-gray-100"
                  )}>
                    <div className="text-center">
                      <div className="text-sm font-bold">{format(day, "dd")}</div>
                      <div className="text-xs">{format(day, "MMM", { locale: de })}</div>
                    </div>
                  </div>
                  <h3 className="font-semibold">{format(day, "EEEE", { locale: de })}</h3>
                </div>
                
                {dayLeads.length > 0 ? (
                  <div className="space-y-2 pl-16">
                    {dayLeads.map((lead) => {
                      const contactTime = lead.nextContactDate 
                        ? format(new Date(lead.nextContactDate), "HH:mm")
                        : "";
                        
                      return (
                        <Card 
                          key={lead.id} 
                          className="overflow-hidden hover:shadow-md cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLeadClick(lead);
                          }}
                        >
                          <CardContent className="p-3 flex items-center">
                            {contactTime && (
                              <div className="text-sm font-semibold w-16">{contactTime}</div>
                            )}
                            <div className="flex-grow">
                              <div className="font-medium">{`${lead.firstName} ${lead.lastName}`}</div>
                              <div className="text-sm text-gray-500">{lead.company}</div>
                            </div>
                            <Badge className={cn(
                              "ml-2", 
                              STATUS_CLASSES[lead.status]
                            )}>
                              {STATUS_LABELS[lead.status]}
                            </Badge>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                ) : (
                  <div className="pl-16 text-gray-500 italic">Keine Termine</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Generate day view calendar
  const renderDayView = () => {
    const dayLeads = getLeadsForDate(leads, currentDate);
    const hours = Array.from({ length: 24 }, (_, i) => i);
    
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b flex justify-between items-center">
          <div className="flex items-center">
            <div className={cn(
              "w-12 h-12 rounded-full flex items-center justify-center mr-4",
              isToday(currentDate) ? "bg-primary text-white" : "bg-gray-100"
            )}>
              <div className="text-center">
                <div className="text-sm font-bold">{format(currentDate, "dd")}</div>
                <div className="text-xs">{format(currentDate, "MMM", { locale: de })}</div>
              </div>
            </div>
            <h3 className="font-semibold text-lg">{format(currentDate, "EEEE, d. MMMM yyyy", { locale: de })}</h3>
          </div>
        </div>
        
        <div className="divide-y">
          {hours.map((hour) => {
            const hourLeads = dayLeads.filter(lead => {
              if (!lead.nextContactDate) return false;
              const date = new Date(lead.nextContactDate);
              return date.getHours() === hour;
            });
            
            return (
              <div key={hour} className="flex">
                <div className="w-16 py-4 px-2 text-center text-sm font-medium text-gray-500 border-r">
                  {hour.toString().padStart(2, '0')}:00
                </div>
                <div className="flex-1 min-h-[60px] p-2">
                  {hourLeads.map((lead) => {
                    const contactTime = lead.nextContactDate 
                      ? format(new Date(lead.nextContactDate), "HH:mm")
                      : "";
                      
                    return (
                      <Card 
                        key={lead.id} 
                        className="mb-2 overflow-hidden hover:shadow-md cursor-pointer"
                        onClick={() => handleLeadClick(lead)}
                      >
                        <CardContent className="p-3 flex items-center">
                          <div className="text-sm font-semibold w-16">{contactTime}</div>
                          <div className="flex-grow">
                            <div className="font-medium">{`${lead.firstName} ${lead.lastName}`}</div>
                            <div className="text-sm text-gray-500">{lead.company}</div>
                          </div>
                          <Badge className={cn(
                            "ml-2", 
                            STATUS_CLASSES[lead.status]
                          )}>
                            {STATUS_LABELS[lead.status]}
                          </Badge>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>
          <Calendar className="h-4 w-4 mr-2" />
          Heute
        </Button>
        
        <div className="flex items-center space-x-4">
          <Button variant="outline" size="icon" onClick={() => navigatePeriod("prev")}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <h2 className="text-xl font-semibold">
            {viewMode === "month" && format(currentDate, "MMMM yyyy", { locale: de })}
            {viewMode === "week" && (
              `${format(weekDays[0], "d.", { locale: de })} - ${format(weekDays[6], "d. MMMM yyyy", { locale: de })}`
            )}
            {viewMode === "day" && format(currentDate, "d. MMMM yyyy", { locale: de })}
          </h2>
          
          <Button variant="outline" size="icon" onClick={() => navigatePeriod("next")}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {viewMode === "month" && renderMonthView()}
      {viewMode === "week" && renderWeekView()}
      {viewMode === "day" && renderDayView()}
    </div>
  );
}
